/**
 * Sidebar configuration driven by CASL / backend permissions (module.feature.access).
 * Aligned with docs/backend_docs/RBAC_Roles_And_Permissions.md
 *
 * Module sidebar: Home, RBAC, Chat, Settings.
 * Feature sidebar: shows features per module; visibility by requiredPermission.
 */

import React from 'react';
import {
    HomeOutlined,
    TeamOutlined,
    UserOutlined,
    MessageOutlined,
    SettingOutlined,
    AppstoreOutlined,
    DashboardOutlined,
    SafetyOutlined,
    FolderOutlined,
    CalendarOutlined,
    ShopOutlined,
    NotificationOutlined,
    ProjectOutlined,
    DollarOutlined,
    CreditCardOutlined,
    ClockCircleOutlined,
    RiseOutlined,
    GlobalOutlined,
    UsergroupAddOutlined,
} from '@ant-design/icons';

import { SidebarIconSvg } from '@components/ui/SidebarIconSvg';
import HomeIcon from '@assets/icons/homeIcon.svg?react';
import ChatIcon from '@assets/icons/chatIcon.svg?react';
import ProjectIcon from '@assets/icons/projectIcon.svg?react';
import TemplateIcon from '@assets/icons/templateIcon.svg?react';

function sidebarSvgIcon(Icon, size = 22) {
    return () =>
        React.createElement(SidebarIconSvg, {
            Icon,
            size,
            className: 'sidebar-icon-svg',
        });
}

/** Default path prefix for tenant app */
const T = (path) => path;

/**
 * Modules for the black module sidebar (icon-only).
 * Icons: Home, RBAC (SafetyOutlined), Chat, Settings.
 * Show module if user has any permission matching permissionPrefix/permissionPrefixes/requiredPermission,
 * or fallbackIfAuthenticated.
 */
/**
 * Module IDs shown in the narrow module sidebar strip (order top → bottom).
 * Roles first, then Home, Projects, Chat, Billing, HR. Remaining visible modules appear under "More".
 */
export const MODULE_SIDEBAR_PRIMARY_IDS = ['roles', 'dashboard', 'projects', 'clients', 'chat', 'billing', 'hr'];

export const MODULE_SIDEBAR_CONFIG = [
    {
        id: 'dashboard',
        path: '/dashboard',
        pathTenant: '/tenant/dashboard',
        label: 'Home',
        icon: sidebarSvgIcon(HomeIcon),
        permissionPrefix: null,
        permissionPrefixes: ['analytics.', 'dashboard.'],
        requiredPermission: 'analytics.admin.view',
        fallbackIfAuthenticated: false,
    },
    {
        id: 'roles',
        path: '/roles',
        pathTenant: '/tenant/roles',
        label: 'Roles',
        icon: SafetyOutlined,
        permissionPrefix: null,
        permissionPrefixes: ['saas.roles.'],
        requiredPermission: null,
        fallbackIfAuthenticated: false,
    },
    {
        id: 'users',
        path: '/users',
        pathTenant: '/tenant/users',
        label: 'User',
        icon: UserOutlined,
        permissionPrefix: null,
        permissionPrefixes: ['saas.users.'],
        requiredPermission: null,
        fallbackIfAuthenticated: false,
    },
    {
        id: 'open-market',
        path: '/open-market',
        pathTenant: '/tenant/open-market',
        defaultPath: '/open-market/overview',
        defaultPathTenant: '/tenant/open-market/overview',
        label: 'Open\nMarket',
        icon: ShopOutlined,
        permissionPrefix: 'open_market.',
        permissionPrefixes: null,
        requiredPermission: null,
        fallbackIfAuthenticated: false,
    },
    {
        id: 'project-requests',
        path: '/open-market/project-requests',
        pathTenant: '/tenant/open-market/project-requests',
        label: 'Project\nRequest',
        icon: ProjectOutlined,
        permissionPrefix: null,
        permissionPrefixes: null,
        requiredPermission: 'project_requests.requests.view',
        fallbackIfAuthenticated: false,
    },
    {
        id: 'chat',
        path: '/chat',
        pathTenant: '/tenant/chat',
        label: 'Chat',
        icon: sidebarSvgIcon(ChatIcon),
        permissionPrefix: 'chat.',
        permissionPrefixes: null,
        requiredPermission: null,
        fallbackIfAuthenticated: false,
    },
    {
        id: 'projects',
        path: '/projects',
        pathTenant: '/tenant/projects',
        defaultPath: '/projects',
        defaultPathTenant: '/tenant/projects',
        label: 'Projects',
        icon: sidebarSvgIcon(ProjectIcon),
        permissionPrefix: 'projects.',
        permissionPrefixes: null,
        requiredPermission: null,
        fallbackIfAuthenticated: false,
    },
    {
        id: 'clients',
        path: '/clients',
        pathTenant: '/tenant/clients',
        defaultPath: '/clients',
        defaultPathTenant: '/tenant/clients',
        label: 'Client',
        icon: UsergroupAddOutlined,
        permissionPrefix: null,
        permissionPrefixes: ['clients.'],
        requiredPermission: 'clients.clients.view',
        fallbackIfAuthenticated: false,
    },
    {
        id: 'sales',
        path: '/sales',
        pathTenant: '/tenant/sales',
        defaultPath: '/sales/leads',
        defaultPathTenant: '/tenant/sales/leads',
        label: 'Sales',
        icon: DollarOutlined,
        permissionPrefix: null,
        permissionPrefixes: null,
        requiredPermission: ['sales.leads.view', 'sales.leads_client.view'],
        fallbackIfAuthenticated: false,
    },
    {
        id: 'billing',
        path: '/billing',
        pathTenant: '/tenant/billing',
        defaultPath: '/billing/requests',
        defaultPathTenant: '/tenant/billing/requests',
        label: 'Billing',
        icon: CreditCardOutlined,
        permissionPrefix: null,
        permissionPrefixes: null,
        requiredPermission: 'billing.requests.view',
        fallbackIfAuthenticated: false,
    },
    {
        id: 'hr',
        path: '/hr',
        pathTenant: '/tenant/hr',
        defaultPath: '/hr/employees',
        defaultPathTenant: '/tenant/hr/employees',
        label: 'HR',
        icon: TeamOutlined,
        permissionPrefix: null,
        permissionPrefixes: ['hr.'],
        requiredPermission: null,
        fallbackIfAuthenticated: false,
    },
    {
        id: 'notifications',
        path: '/notifications',
        pathTenant: '/tenant/notifications',
        defaultPath: '/notifications',
        defaultPathTenant: '/tenant/notifications',
        label: 'Notify',
        icon: NotificationOutlined,
        permissionPrefix: 'notifications.',
        permissionPrefixes: null,
        requiredPermission: null,
        fallbackIfAuthenticated: false,
    },
    {
        id: 'settings',
        path: '/settings',
        pathTenant: '/tenant/settings',
        label: 'Settings',
        icon: SettingOutlined,
        permissionPrefix: 'settings.',
        permissionPrefixes: null,
        requiredPermission: null,
        fallbackIfAuthenticated: false,
    },
    {
        id: 'project-templates',
        path: '/project-templates',
        pathTenant: '/tenant/project-templates',
        label: 'Templates',
        icon: sidebarSvgIcon(TemplateIcon),
        permissionPrefix: null,
        permissionPrefixes: null,
        requiredPermission: { operator: 'and', permissions: ['projects.templates.view', 'projects.templates.manage'] },
        fallbackIfAuthenticated: false,
    },
    {
        id: 'assets-manager',
        path: '/assets-manager',
        pathTenant: '/tenant/assets-manager',
        defaultPath: '/assets-manager',
        defaultPathTenant: '/tenant/assets-manager',
        label: 'Assets\nManager',
        icon: AppstoreOutlined,
        permissionPrefix: null,
        permissionPrefixes: ['assets_manager.', 'assets.'],
        requiredPermission: null,
        fallbackIfAuthenticated: true,
    },
];

/**
 * Features per module (for the white feature sidebar).
 * Shown only if user has the requiredPermission (or null = no permission gate).
 */
export const FEATURE_SIDEBAR_CONFIG = {
    dashboard: {
        title: 'Home',
        features: [
            {
                key: '/dashboard',
                keyTenant: '/tenant/dashboard',
                label: 'Overview',
                icon: sidebarSvgIcon(HomeIcon),
                requiredPermission: null,
            },
        ],
    },
    roles: {
        title: 'Roles',
        features: [
            {
                key: '/roles',
                keyTenant: '/tenant/roles',
                label: 'Overview',
                icon: DashboardOutlined,
                requiredPermission: 'saas.roles.view',
            },
        ],
    },
    users: {
        title: 'User',
        features: [
            {
                key: '/users',
                keyTenant: '/tenant/users',
                label: 'Overview',
                icon: DashboardOutlined,
                requiredPermission: 'saas.users.view',
            },
        ],
    },
    sales: {
        title: 'Sales',
        features: [
            {
                key: '/sales/leads',
                keyTenant: '/tenant/sales/leads',
                label: 'Leads',
                icon: UserOutlined,
                requiredPermission: ['sales.leads.view', 'sales.leads_client.view'],
            },
        ],
    },
    clients: {
        title: 'Client',
        features: [
            {
                key: '/clients',
                keyTenant: '/tenant/clients',
                label: 'All',
                icon: UsergroupAddOutlined,
                requiredPermission: 'clients.clients.view',
            },
            {
                key: '/clients/inhouse',
                keyTenant: '/tenant/clients/inhouse',
                label: 'Inhouse Client',
                icon: HomeOutlined,
                requiredPermission: 'clients.clients.view',
            },
        ],
    },
    billing: {
        title: 'Billing',
        features: [
            {
                key: '/billing/requests',
                keyTenant: '/tenant/billing/requests',
                label: 'Requests',
                icon: CreditCardOutlined,
                requiredPermission: 'billing.requests.view',
            },
        ],
    },
    hr: {
        title: 'HR',
        features: [
            {
                key: '/hr/employees',
                keyTenant: '/tenant/hr/employees',
                label: 'Employees',
                icon: TeamOutlined,
                requiredPermission: 'hr.employees.view',
            },
            {
                key: '/hr/schedules',
                keyTenant: '/tenant/hr/schedules',
                label: 'Schedules',
                icon: CalendarOutlined,
                requiredPermission: 'hr.schedule.view',
            },
            {
                key: '/hr/leave?view=my',
                keyTenant: '/tenant/hr/leave?view=my',
                label: 'My Leaves',
                icon: CalendarOutlined,
                check: (can) => can('hr.leave.approve') && can('hr.leave.request'),
            },
            {
                key: '/hr/leave?view=employee',
                keyTenant: '/tenant/hr/leave?view=employee',
                label: 'Employee Leaves',
                icon: CalendarOutlined,
                check: (can) => can('hr.leave.approve'),
            },
            {
                key: '/hr/leave',
                keyTenant: '/tenant/hr/leave',
                label: 'Leave',
                icon: CalendarOutlined,
                check: (can) => !can('hr.leave.approve') && can('hr.leave.request'),
            },
            {
                key: '/hr/attendance',
                keyTenant: '/tenant/hr/attendance',
                label: 'Attendance',
                icon: ClockCircleOutlined,
                requiredPermission: 'hr.attendance.view',
            },
            {
                key: '/hr/performance',
                keyTenant: '/tenant/hr/performance',
                label: 'Performance',
                icon: RiseOutlined,
                requiredPermission: 'hr.performance.view',
            },
            {
                key: '/hr/salary',
                keyTenant: '/tenant/hr/salary',
                label: 'Salary',
                icon: DollarOutlined,
                requiredPermission: 'hr.salary.view',
            },
            {
                key: '/hr/removal-requests',
                keyTenant: '/tenant/hr/removal-requests',
                label: 'Removal Requests',
                icon: UserOutlined,
                requiredPermission: 'hr.employees.view',
            },
        ],
    },
    chat: {
        title: 'Chat',
        features: [], // Filled from GET /api/v1/chat in layout (list of persons/chats)
    },
    'project-requests': {
        title: 'Project Requests',
        features: [
            {
                key: '/open-market/project-requests',
                keyTenant: '/tenant/open-market/project-requests',
                label: 'Overview',
                icon: DashboardOutlined,
                requiredPermission: null,
            },
        ],
    },
    'open-market': {
        title: 'Open Market',
        features: [
            {
                key: '/open-market/overview',
                keyTenant: '/tenant/open-market/overview',
                label: 'Overview',
                icon: DashboardOutlined,
                requiredPermission: null,
            },
        ],
    },
    projects: {
        title: 'Projects',
        features: [
            {
                key: '/projects',
                keyTenant: '/tenant/projects',
                label: 'Overview',
                icon: sidebarSvgIcon(ProjectIcon),
                requiredPermission: null,
            },
        ],
    },
    notifications: {
        title: 'Notifications',
        features: [
            {
                key: '/notifications',
                keyTenant: '/tenant/notifications',
                label: 'Overview',
                icon: DashboardOutlined,
                requiredPermission: null,
            },
            {
                key: '/notifications/unread',
                keyTenant: '/tenant/notifications/unread',
                label: 'Unread',
                icon: DashboardOutlined,
                requiredPermission: null,
            },
            {
                key: '/notifications/read',
                keyTenant: '/tenant/notifications/read',
                label: 'Read',
                icon: DashboardOutlined,
                requiredPermission: null,
            },
        ],
    },
    settings: {
        title: 'Settings',
        features: [
            {
                key: '/settings',
                keyTenant: '/tenant/settings',
                label: 'General',
                icon: SettingOutlined,
                requiredPermission: null,
            },
            {
                key: '/settings/origins',
                keyTenant: '/tenant/settings/origins',
                label: 'Source Origins',
                icon: GlobalOutlined,
                requiredPermission: null,
            },
        ],
    },
    'assets-manager': {
        title: 'Assets Manager',
        features: [
            {
                key: '/assets-manager',
                keyTenant: '/tenant/assets-manager',
                label: 'Overview',
                icon: AppstoreOutlined,
                requiredPermission: null,
            },
        ],
    },
    'project-templates': {
        title: 'Templates',
        features: [
            {
                key: '/project-templates',
                keyTenant: '/tenant/project-templates',
                label: 'Overview',
                icon: sidebarSvgIcon(TemplateIcon),
                requiredPermission: { operator: 'and', permissions: ['projects.templates.view', 'projects.templates.manage'] },
            },
        ],
    },
};

/**
 * Get the menu key for a module (used for navigation and selection).
 * Uses defaultPath/defaultPathTenant when set, so clicking the module goes to the first feature.
 */
export const getModuleMenuKey = (moduleId, isTenantRoute) => {
    const mod = MODULE_SIDEBAR_CONFIG.find((m) => m.id === moduleId);
    if (!mod) return null;
    return isTenantRoute ? (mod.defaultPathTenant ?? mod.pathTenant) : (mod.defaultPath ?? mod.path);
};

/**
 * Resolve which module id matches the current path (for feature sidebar context).
 * Checks module path first (longer paths first so e.g. /open-market/project-requests matches project-requests, not open-market),
 * then each module's feature paths so e.g. /permissions activates People module.
 */
export const getModuleIdFromPath = (pathname, isTenantRoute) => {
    const path = pathname.replace(/^\/tenant/, '') || '/';
    const fullPath = pathname;

    const modulesByPathLength = [...MODULE_SIDEBAR_CONFIG].sort((a, b) => {
        const baseA = isTenantRoute ? (a.pathTenant || '').replace(/^\/tenant/, '') : a.path || '';
        const baseB = isTenantRoute ? (b.pathTenant || '').replace(/^\/tenant/, '') : b.path || '';
        return baseB.length - baseA.length;
    });

    for (const mod of modulesByPathLength) {
        const base = isTenantRoute ? (mod.pathTenant || '').replace(/^\/tenant/, '') : mod.path;
        if (base && (path === base || path.startsWith(base + '/'))) return mod.id;

        const featureConfig = FEATURE_SIDEBAR_CONFIG[mod.id];
        if (featureConfig?.features) {
            for (const f of featureConfig.features) {
                const featurePath = isTenantRoute ? f.keyTenant : f.key;
                if (featurePath && fullPath === featurePath) return mod.id;
            }
        }
    }
    return 'dashboard';
};
